import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@material-ui/core';
import { isSessionLoggingOut } from '../../../../utils/sessionLogout';
import useFlussoImportHaccp from './useFlussoImportHaccp';
import useStili, { ID_TITOLO } from './stili';
import SceltaFile from './SceltaFile';
import VerificaInCorso from './VerificaInCorso';
import FileNonConforme from './FileNonConforme';
import ErroreTecnico, { esitoIgnoto } from './ErroreTecnico';
import ConfermaImport from './ConfermaImport';

const trattieniFile = (event) => event.preventDefault();

/**
 * The import of an Export JSON from HACCP Agent into a Gestione Operativa: the
 * choice of the file, its check, and the confirmation before the write.
 *
 * Mount it to open it and unmount it to close it, so every opening starts from
 * the choice of the file. `verifica` and `applica` are described in
 * `useFlussoImportHaccp`; `onImportato` runs once the write succeeded.
 * `onClose` receives `{ scritturaIncerta }`, true when the modal closes on a
 * write whose outcome is unknown: the GeOp may have been replaced all the same.
 */
const ModalImportHaccpAgent = ({ verifica, applica, onClose, onImportato }) => {
  const stili = useStili();
  const flusso = useFlussoImportHaccp({ verifica, applica, onImportato });
  const inScrittura = flusso.passo === 'scrittura';

  const chiudi = () => {
    flusso.interrompi();
    onClose({
      scritturaIncerta:
        flusso.passo === 'erroreTecnico' &&
        esitoIgnoto(flusso.fase, flusso.status),
    });
  };

  // A step that replaces the one holding the focus would drop it on the
  // dialog's edge: it goes to the new step's title, which is read out first.
  // The write shows the confirmation again, where the focus already is.
  const vista = inScrittura ? 'conferma' : flusso.passo;
  useEffect(() => {
    document.getElementById(ID_TITOLO)?.focus();
  }, [vista]);

  // A call that failed because the session expired leaves the session-expired
  // dialog alone on screen: under it, this one would offer a retry that cannot
  // succeed.
  if (flusso.passo === 'erroreTecnico' && isSessionLoggingOut()) return null;

  const passi = {
    selezione: () => (
      <SceltaFile
        motivoScarto={flusso.motivoScarto}
        onScegli={flusso.scegliFile}
        onAnnulla={chiudi}
      />
    ),
    verifica: () => <VerificaInCorso file={flusso.file} onAnnulla={chiudi} />,
    nonConforme: () => (
      <FileNonConforme
        file={flusso.file}
        errori={flusso.errori}
        onCaricaAltroFile={flusso.caricaAltroFile}
        onChiudi={chiudi}
      />
    ),
    erroreTecnico: () => (
      <ErroreTecnico
        fase={flusso.fase}
        file={flusso.file}
        status={flusso.status}
        messaggio={flusso.messaggio}
        onRiprova={flusso.riprova}
        onChiudi={chiudi}
      />
    ),
    conferma: () => (
      <ConfermaImport
        file={flusso.file}
        riepilogo={flusso.riepilogo}
        inScrittura={inScrittura}
        onConferma={flusso.conferma}
        onAnnulla={chiudi}
      />
    ),
  };

  return (
    <Dialog
      open
      // Not while the write runs: closing the window would not stop it.
      onClose={inScrittura ? undefined : chiudi}
      disableBackdropClick={inScrittura}
      disableEscapeKeyDown={inScrittura}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: stili.paper }}
      aria-labelledby={ID_TITOLO}
      // A file let go anywhere but the drop area, the title or the backdrop
      // among them, would otherwise open in the browser in place of the page.
      onDragOver={trattieniFile}
      onDrop={trattieniFile}
    >
      {passi[vista]()}
    </Dialog>
  );
};

ModalImportHaccpAgent.propTypes = {
  verifica: PropTypes.func.isRequired,
  applica: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onImportato: PropTypes.func.isRequired,
};

export default ModalImportHaccpAgent;
